// Vendor Libs
import React, {useEffect} from 'react';

// Material UI
import Fab from '@material-ui/core/Fab';
import AddIcon from '@material-ui/icons/Add';
import {makeStyles} from '@material-ui/core/styles';

// Dish Tech React
import {Overlay} from '@dishtech-react/core';
import {useOverlay, useService} from '@dishtech-react/hooks';
import {GetRippleRoles, areRolesAuthorized} from '@dishtech-react/utils';

// Components
import DeleteFolderConfirmation from './delete-folder-confirmation';
import FolderForm from './folder-form';
import FolderTree from './folder-tree';

// Hooks
import {useFolder} from '../../hooks/use-folder';
import Progress from '../helpers/progress';

const useStyles = makeStyles(_theme => ({
  addButton: {
    position: 'fixed',
    top: '96px',
    right: '32px',
  },
}));

const SERVICE_KEY = 'FOLDER';

const Folder = () => {
  const rippleRoles = GetRippleRoles();
  const classes = useStyles();
  const {closeOverlay: closeCreateOverlay, overlayId: createOverlayId} = useOverlay({
    id: 'folder_create',
  });
  const {closeOverlay: closeViewOverlay, overlayId: viewOverlayId} = useOverlay({
    id: 'folder_view',
  });
  const {closeOverlay: closeUpdateOverlay, overlayId: updateOverlayId} = useOverlay({
    id: 'folder_update',
  });
  const {closeOverlay: closeDeleteOverlay, overlayId: deleteOverlayId} = useOverlay({
    id: 'folder_delete',
  });
  const {serviceContent, registerService, unregisterService} = useService(SERVICE_KEY);
  const {createFolder, mode, viewFolder} = useFolder();
  const {featuredFolders} = serviceContent;

  useEffect(() => {
    if (areRolesAuthorized(rippleRoles, 'FOLDER', ['CREATE', 'DELETE', 'READ', 'UPDATE'])) {
      registerService();

      return () => unregisterService();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClick = folderData => {
    viewFolder(folderData);
  };

  const onAddClick = () => {
    createFolder();
  };

  return (
    <>
      {featuredFolders && featuredFolders.length ? (
        <>
          <FolderTree folderData={featuredFolders} label="Featured Folders" onClick={onClick} />

          {mode === 'view' ? (
            <Overlay closeOnBlur={true} id={viewOverlayId} title="View Folder">
              <FolderForm rippleRoles={rippleRoles} onCancel={closeViewOverlay} />
            </Overlay>
          ) : null}

          {mode === 'create' ? (
            <Overlay id={createOverlayId} title="Create Folder" wide={true}>
              <FolderForm rippleRoles={rippleRoles} onCancel={closeCreateOverlay} onSave={closeCreateOverlay} />
            </Overlay>
          ) : null}

          {mode === 'update' ? (
            <Overlay id={updateOverlayId} title="Update Folder" wide={true}>
              <FolderForm rippleRoles={rippleRoles} onCancel={closeUpdateOverlay} onSave={closeUpdateOverlay} />
            </Overlay>
          ) : null}

          <Overlay id={deleteOverlayId} title="Delete Folder">
            <DeleteFolderConfirmation onCancel={closeDeleteOverlay} onDelete={closeDeleteOverlay} />
          </Overlay>

          <Fab
            className={classes.addButton}
            color="primary"
            disabled={!areRolesAuthorized(rippleRoles, 'FOLDER', ['CREATE'])}
            size="medium"
            onClick={onAddClick}
          >
            <AddIcon />
          </Fab>
        </>
      ) : (
        <Progress title="Featured Folders" />
      )}
    </>
  );
};

export default Folder;
